import { FC, useMemo, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import FormFieldsContainer from "../../../components/ui/FormFieldsContainer";
import { FormField, ModalDialog, Numeric, Select } from "@rothoblaas/design-system";
import useI18n from "../../../hooks/useTranslations";
import { type TimberClass } from "../../../http/beamReinforcement/types/timberClass";
import { BeamReinforcementConfigurationFormValues } from "../../../http/beamReinforcement/useAutoSaveBeamReinforcementConfiguration";
import styles from "./BeamsCard.module.css";

type ReinforcementSidesOption = { value: 1 | 2; label: string };

type BeamsCardProps = {
  defaultValues: BeamReinforcementConfigurationFormValues;
  timbers: TimberClass[];
  disabled?: boolean;
};

const BeamsCard: FC<BeamsCardProps> = ({ defaultValues, timbers, disabled = false }) => {
  const i18n = useI18n();
  const t = i18n.translation.beamReinforcementModule.calculation;
  const {
    control,
    formState: { errors },
  } = useFormContext<BeamReinforcementConfigurationFormValues>();

  const existingHeight = useWatch({ control, name: "existingHeight" });
  const [isLengthHelpOpen, setIsLengthHelpOpen] = useState(false);

  const REINFORCEMENT_SIDES_OPTIONS: ReinforcementSidesOption[] = [
    { value: 1, label: t.reinforcementSidesOne },
    { value: 2, label: t.reinforcementSidesTwo },
  ];

  const timberSelect = (
    name: "existingMaterial" | "reinforcementMaterial",
    label: string,
    defaultValue: TimberClass | null,
  ) => (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={{ required: true }}
      render={({ field, fieldState }) => (
        <FormField label={label} error={fieldState.error}>
          {({ labelId, isInvalid }) => (
            <Select<TimberClass, false>
              {...field}
              inputId={labelId}
              isDisabled={disabled}
              value={timbers.find((timberClass) => timberClass.name === field.value?.name) ?? null}
              options={timbers}
              getOptionValue={(option) => option.name}
              getOptionLabel={(option) => option.name}
              isInvalid={isInvalid}
              onChange={(selectedOption) => field.onChange(selectedOption ?? null)}
            />
          )}
        </FormField>
      )}
    />
  );

  const dimensionField = (
    name: "existingBase" | "existingHeight" | "reinforcementBase" | "reinforcementHeight",
    label: string,
    defaultValue: number | null,
    minValue: number,
    maxValue?: number,
  ) => (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={{ min: minValue, ...(maxValue !== undefined ? { max: maxValue } : {}) }}
      render={({ field }) => (
        <FormField label={label} error={errors[name]}>
          {({ labelId, isInvalid }) => (
            <Numeric
              {...field}
              id={labelId}
              value={field.value}
              suffix={` ${i18n.translation.common.unitMm}`}
              decimalScale={0}
              step={10}
              min={minValue}
              disabled={disabled}
              allowNegative={false}
              isInvalid={isInvalid}
              onValueChange={(value) => field.onChange(value)}
            />
          )}
        </FormField>
      )}
    />
  );

  // Optional — only needed to unlock the 3D preview (extrudes the 2D cross-section along the beam axis).
  const lengthField = (name: "existingLength" | "reinforcementLength", label: string, defaultValue: number | null) => (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={{ min: 0.1 }}
      render={({ field }) => (
        <FormField
          label={label}
          error={errors[name]}
          isOptional
          showHelp={{ onOpen: () => setIsLengthHelpOpen(true) }}
        >
          {({ labelId, isInvalid }) => (
            <Numeric
              {...field}
              id={labelId}
              value={field.value}
              suffix={` ${i18n.translation.common.unitM}`}
              decimalScale={2}
              step={0.1}
              min={0.1}
              disabled={disabled}
              allowNegative={false}
              isInvalid={isInvalid}
              onValueChange={(value) => field.onChange(value)}
            />
          )}
        </FormField>
      )}
    />
  );

  const reinforcementHeightMax = useMemo(() => existingHeight ?? undefined, [existingHeight]);

  return (
    <div>
      <FormFieldsContainer fullWidth>
        <div className={styles.sectionTitle}>{t.existingBeamTitle}</div>
        {timberSelect("existingMaterial", t.strengthClass, defaultValues.existingMaterial)}
        {dimensionField("existingBase", t.base, defaultValues.existingBase, 40)}
        {dimensionField("existingHeight", t.height, defaultValues.existingHeight, 60)}
        {lengthField("existingLength", t.existingLength, defaultValues.existingLength)}

        <div className={styles.sectionTitle}>{t.reinforcementBeamTitle}</div>
        {timberSelect("reinforcementMaterial", t.strengthClass, defaultValues.reinforcementMaterial)}
        {dimensionField("reinforcementBase", t.base, defaultValues.reinforcementBase, 40)}
        {dimensionField("reinforcementHeight", t.height, defaultValues.reinforcementHeight, 60, reinforcementHeightMax)}
        {lengthField("reinforcementLength", t.reinforcementLength, defaultValues.reinforcementLength)}

        <Controller
          name="reinforcementSides"
          control={control}
          defaultValue={defaultValues.reinforcementSides}
          rules={{ required: true }}
          render={({ field }) => (
            <FormField label={t.reinforcementSides} error={errors.reinforcementSides}>
              {({ labelId, isInvalid }) => (
                <Select<ReinforcementSidesOption, false>
                  {...field}
                  inputId={labelId}
                  isDisabled={disabled}
                  value={REINFORCEMENT_SIDES_OPTIONS.find((option) => option.value === field.value) ?? null}
                  options={REINFORCEMENT_SIDES_OPTIONS}
                  getOptionValue={(option) => `${option.value}`}
                  getOptionLabel={(option) => option.label}
                  isInvalid={isInvalid}
                  onChange={(selectedOption) => field.onChange(selectedOption?.value ?? null)}
                />
              )}
            </FormField>
          )}
        />
      </FormFieldsContainer>

      {isLengthHelpOpen && (
        <ModalDialog onClose={() => setIsLengthHelpOpen(false)} title={i18n.translation.common.help}>
          <div className={styles.helpContent}>{t.lengthOptionalHint}</div>
        </ModalDialog>
      )}
    </div>
  );
};

export default BeamsCard;
